import classNames from "classnames";
import * as React from "react";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { ConfigTooltip } from "./app-settings-config-tooltip";
import { ConfigPaneKey } from "./config-categories";
import {
  ConfigCategoryContext,
  ConfigChangeContext,
  IConfigChangeRegistry,
  IConfigSectionScope,
  readFieldPath
} from "./config-change-tracking";
import {
  ConfigFieldRevealContext,
  ConfigPaneContext,
  IConfigFieldReveal,
  IConfigSearchTarget,
  useConfigSearchTarget
} from "./config-search-context";
import { IConfigSearchEntryDraft, reactNodeText } from "./config-search-index";
import "./config-pane-parts.scss";

/**
 * The shape every configuration pane is built from: a support toggle, a two-column body,
 * and a stack of titled sections.
 *
 * Each pane used to spell this out inline — twelve lines of nested divs carrying
 * `kuika_app-settings__content__container__config-manager__topic-title` and its siblings,
 * repeated ~45 times with the same `style={{ marginBottom: 24 }}`. Those classes are styled
 * in `styling.scss` from hardcoded hex (`#e1e5eb` dividers, `#a3b0cc` descriptions,
 * `rgba(0, 0, 0, 0.64)` rail labels), which is why the panes stayed light on the dark theme
 * however well the card around them was themed.
 *
 * The legacy wrapper class stays on the pane container — the ~140 per-setting files below
 * still hang their rows off it. These parts replace only the chrome the panes own.
 */

export interface IConfigSupportToggleProps {
  /** "Support Web UI", "Support IOS UI" — the platform this pane configures. */
  label: string;
  description?: React.ReactNode;
  checked: boolean;
  onChange: () => void;
  switchTestId?: string;
  descriptionTestId?: string;
  /** On the label text. The Android and Desktop panes' suites read the wording. */
  labelTestId?: string;
  /**
   * Renders it as the first block of the pane's right column rather than as a full-width
   * strip above the body. That is where it belongs once the pane has a rail: the switch is
   * a property OF the platform, and it stays visible while the rail scrolls.
   */
  aside?: boolean;
}

/**
 * The control that turns a whole platform on. It is not a field: when it is off the pane
 * has nothing to configure, so the rail and the body are hidden rather than disabled.
 */
export const ConfigSupportToggle = ({
  label,
  description,
  checked,
  onChange,
  switchTestId,
  descriptionTestId,
  labelTestId,
  aside = false
}: IConfigSupportToggleProps) => (
  <div className={classNames("kuika-config-support", { "kuika-config-support--aside": aside })}>
    {aside && <KuikaTextMedium className="kuika-config-support__title">Platform</KuikaTextMedium>}
    <KuikaSwitch
      label={label}
      checked={checked}
      onChange={onChange}
      dataTestId={switchTestId}
      labelTestId={labelTestId}
    />
    {description && (
      <div className="kuika-config-support__description" data-testid={descriptionTestId}>
        {description}
      </div>
    )}
  </div>
);

export interface IConfigPaneBodyProps {
  children: React.ReactNode;
  /** The right rail: title/icon/splash settings that read as properties OF the platform. */
  secondary?: React.ReactNode;
  /** False while the platform's support toggle is off. */
  visible?: boolean;
  /** The workspace twin has no support strip above it, so it needs no rule under one. */
  bordered?: boolean;
  className?: string;
}

export const ConfigPaneBody = ({
  children,
  secondary,
  visible = true,
  bordered = true,
  className
}: IConfigPaneBodyProps) => (
  <div
    className={classNames(
      "kuika-config-pane",
      { "kuika-config-pane--bordered": bordered, "kuika-config-pane--hidden": !visible },
      className
    )}
  >
    <div className="kuika-config-pane__primary">{children}</div>
    {secondary && <div className="kuika-config-pane__secondary">{secondary}</div>}
  </div>
);

export interface IConfigSectionProps {
  /**
   * Title Case. The ALL-CAPS look is the stylesheet's now, not the string's, so the same
   * label can serve the rail (as written) and the heading (uppercased) without two copies.
   */
  title: string;
  description?: React.ReactNode;
  titleTestId?: string;
  descriptionTestId?: string;
  children?: React.ReactNode;
  /**
   * A heading INSIDE the right rail rather than a section of the pane: the rail is one
   * column of stacked settings, so its headings carry no rule and no gutter of their own.
   */
  rail?: boolean;
  /** The category this section IS. Reaches the DOM, and the E2E suite resolves on it. */
  categoryId?: string;
  /**
   * False for every category except the selected one.
   *
   * It hides with CSS and never unmounts: one antd `Form` spans all five panes and all of
   * their categories, and `handleSubmit` rebuilds the configuration from `getFieldsValue()`
   * — a section taken out of the tree is a field silently written back as empty.
   */
  active?: boolean;
  /** A caveat about the section as a whole, rather than about one of its fields. */
  warning?: React.ReactNode;
  className?: string;
}

export const ConfigSection = ({
  title,
  description,
  titleTestId,
  descriptionTestId,
  children,
  rail = false,
  categoryId,
  active = true,
  warning,
  className
}: IConfigSectionProps) => {
  const pane: ConfigPaneKey | undefined = React.useContext(ConfigPaneContext);

  // Memoized because every field inside reads it: ~300 of them are mounted at once, and a
  // fresh object here would re-render all of them on every render of any section.
  const scope: IConfigSectionScope = React.useMemo(
    (): IConfigSectionScope => ({ categoryId, title, active }),
    [categoryId, title, active]
  );

  const descriptionText: string = reactNodeText(description);
  const draft: IConfigSearchEntryDraft | undefined = React.useMemo((): IConfigSearchEntryDraft | undefined => {
    if (!pane) return undefined;
    return {
      kind: "section",
      pane,
      categoryId,
      sectionTitle: title,
      label: title,
      description: descriptionText,
      terms: [],
      termsOpenDialog: false
    };
  }, [pane, categoryId, title, descriptionText]);

  const search: IConfigSearchTarget = useConfigSearchTarget(draft, {
    key: `section|${pane ?? ""}|${categoryId ?? ""}|${title}|${descriptionText}`,
    // A rail section belongs to the platform rather than to a category, so it is always
    // drawn; a category's section is drawn only while the rail is pointing at it.
    visible: active
  });

  return (
    <section
      className={classNames(
        "kuika-config-section",
        {
          "kuika-config-section--rail": rail,
          "kuika-config-section--inactive": !active,
          "kuika-config-section--revealed": search.revealed
        },
        className
      )}
      data-config-category={categoryId}
      {...search.anchorProps}
    >
      <KuikaTextMedium className="kuika-config-section__title" data-testid={titleTestId}>
        {title}
      </KuikaTextMedium>
      {description && (
        <KuikaSmallText className="kuika-config-section__description" data-testid={descriptionTestId}>
          {description}
        </KuikaSmallText>
      )}
      {warning && <div className="kuika-config-section__warning">{warning}</div>}
      <ConfigCategoryContext.Provider value={scope}>{children}</ConfigCategoryContext.Provider>
    </section>
  );
};

export interface IConfigSectionWarningProps {
  children: React.ReactNode;
  dataTestId?: string;
}

/**
 * A caveat about a whole section rather than about one of its fields — a pair of switches
 * that is dangerous only in combination, say. `ConfigSection`'s own `warning` prop sits
 * above the fields; this one is for a warning a caller renders conditionally among them.
 */
export const ConfigSectionWarning = ({ children, dataTestId }: IConfigSectionWarningProps) => (
  <div className="kuika-config-section__warning" data-testid={dataTestId}>
    {children}
  </div>
);

export interface IConfigFieldProps {
  /** The fixed left column. Omit for a control that is its own label, like a switch. */
  label?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  /** Sits under the control — "ADD NEW · 3 saved", a character budget, a format note. */
  hint?: React.ReactNode;
  /** Sits under the control in a warning colour — a stale binding, a risky combination. */
  warning?: React.ReactNode;
  /**
   * Sits under the control in the error colour. For a control antd cannot validate — a file
   * chosen through a popover has no `Form.Item` to hang `rules` off, so the dialog decides
   * when it is missing and says so here.
   */
  error?: React.ReactNode;
  /**
   * Key into `sd.configLocalizations` for the (i) after the label. Omit for no tooltip.
   *
   * A row with no label — a control that names itself — puts it in the meta column instead,
   * since there is nothing for it to sit against.
   */
  tooltipContentKey?: string;
  /** Present only on a row that overrides its parent; hands the parent's value back. */
  onRestore?: () => void;
  /**
   * The row is in the pane's right column, where labels sit on a heavier step so they hold
   * against the narrower measure.
   */
  aside?: boolean;
  labelTestId?: string;
  className?: string;
  /**
   * The antd path this row edits. Only needed where it cannot be read off a `Form.Item`
   * among the children — `ConfigSettingSelect` renders its own, so it says so explicitly.
   */
  formInputName?: string[];
  /**
   * The field names inside the dialog this row opens, for the search.
   *
   * A dialog is mounted only while it is open, so nothing about its contents can be
   * collected the way a label is. The list lives next to the dialog that owns it, as
   * `*_SEARCH_TERMS`.
   */
  searchTerms?: string[];
  /**
   * Whether a hit on those terms should OPEN the dialog.
   *
   * Off by default, because only the row that renders an opener can answer the request:
   * `ConfigSettingSelect` does, and the REST API rows — which list what their dialog holds
   * but drive their own control — do not. A row that cannot open anything must not be
   * badged as though it could.
   */
  searchTermsOpenDialog?: boolean;
}

/**
 * One setting: a label carrying its (i), the control, and the restore affordance that hangs
 * off the control on the rows that override a parent value.
 *
 * The ~140 per-setting files draw this today as three sibling spans carrying
 * `config-manager__property`, `kuika_form_label k_Medium-G2-Left-2 property__text` and two
 * `config-manager__property__tooltip`s, styled from hardcoded hex in `styling.scss`. Those
 * classes stay where they are — run-settings and every unmigrated leaf still hang off them
 * — and this is what replaces them one leaf at a time.
 */
export const ConfigField = ({
  label,
  required = false,
  children,
  hint,
  warning,
  error,
  tooltipContentKey,
  onRestore,
  aside = false,
  labelTestId,
  className,
  formInputName,
  searchTerms,
  searchTermsOpenDialog = false
}: IConfigFieldProps) => {
  const registry: IConfigChangeRegistry = React.useContext(ConfigChangeContext);
  const scope: IConfigSectionScope = React.useContext(ConfigCategoryContext);
  const pane: ConfigPaneKey | undefined = React.useContext(ConfigPaneContext);
  const categoryId: string | undefined = scope.categoryId;
  const path: string | undefined = React.useMemo(
    (): string | undefined => readFieldPath(formInputName, children),
    // The path is a property of the markup, not of the value the control currently holds:
    // re-reading it on every keystroke would walk the subtree for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formInputName ? formInputName.join(".") : undefined]
  );

  // The rail dots a category without knowing what is inside it: every field says so on
  // mount instead, so the two can never drift the way a hand-written key list would.
  React.useEffect((): (() => void) | undefined => {
    if (!categoryId || !path) return undefined;
    registry.registerField(categoryId, path);
    return (): void => registry.unregisterField(categoryId, path);
  }, [registry, categoryId, path]);

  // The search index is filled the same way, for the same reason. A row with no label is a
  // control that labels itself — a switch — and there is nothing to search it by.
  const labelText: string = reactNodeText(label);
  const termsKey: string = (searchTerms ?? []).join("\u0000");
  const draft: IConfigSearchEntryDraft | undefined = React.useMemo((): IConfigSearchEntryDraft | undefined => {
    if (!pane || labelText === "") return undefined;
    return {
      kind: "field",
      pane,
      categoryId,
      sectionTitle: scope.title,
      label: labelText,
      tooltipKey: tooltipContentKey,
      terms: termsKey === "" ? [] : termsKey.split("\u0000"),
      termsOpenDialog: searchTermsOpenDialog
    };
  }, [pane, categoryId, scope.title, labelText, tooltipContentKey, termsKey, searchTermsOpenDialog]);

  const search: IConfigSearchTarget = useConfigSearchTarget(draft, {
    key: `field|${pane ?? ""}|${categoryId ?? ""}|${scope.title}|${labelText}|${tooltipContentKey ?? ""}|${termsKey}|${searchTermsOpenDialog}`,
    visible: scope.active
  });
  const isChanged: boolean = path !== undefined && registry.changedPaths.has(path);
  // The (i) rides with the label, which is the thing it explains. A control that labels
  // itself — a bare switch — has nothing to ride, so its (i) stays in the meta column.
  const tooltipInMeta: boolean = tooltipContentKey !== undefined && label === undefined;

  // Passed down rather than acted on here: whether this row HAS a dialog, and which of its
  // saved settings to open, is known only by the leaf that drew it.
  const fieldReveal: IConfigFieldReveal = React.useMemo(
    (): IConfigFieldReveal => ({ wantsDialog: search.wantsDialog, token: search.revealToken }),
    [search.wantsDialog, search.revealToken]
  );

  return (
    <div
      className={classNames(
        "kuika-config-field",
        { "kuika-config-field--aside": aside, "kuika-config-field--revealed": search.revealed },
        className
      )}
      {...search.anchorProps}
    >
      {label !== undefined && (
        <span
          className="kuika-config-field__label"
          data-testid={labelTestId}
          data-changed={isChanged ? "true" : undefined}
        >
          {/* The mark hugs the text rather than the 148px column: a background on the column
              itself would be a wide orange block, and padding on it would shove the control.
              Marked, it is also the undo — the snapshot already holds what this field was,
              so the thing pointing at the edit is the shortest way to drop it. */}
          {/* The words are boxed off from the (i) beside them so the icon gets a column of
              its own: left to flow inline, a label that fills the column strands it alone on
              a second line. Inside the box the text is still inline, which is what keeps the
              mark one chip per line. */}
          <span className="kuika-config-field__label-body">
            {isChanged && path !== undefined ? (
              <KuikaTooltip content="Undo this change" position="top">
                <button
                  type="button"
                  className="kuika-config-field__label-text kuika-config-field__label-text--changed"
                  aria-label="Undo this change"
                  onClick={(): void => registry.revertPaths([path])}
                >
                  {label}
                </button>
              </KuikaTooltip>
            ) : (
              <span className="kuika-config-field__label-text">{label}</span>
            )}
            {required && <span className="kuika-config-field__required">*</span>}
          </span>
          {tooltipContentKey && <ConfigTooltip contentKey={tooltipContentKey} />}
        </span>
      )}
      <div className="kuika-config-field__control">
        <ConfigFieldRevealContext.Provider value={fieldReveal}>{children}</ConfigFieldRevealContext.Provider>
        {hint && <div className="kuika-config-field__hint">{hint}</div>}
        {warning && <div className="kuika-config-field__warning">{warning}</div>}
        {error && <div className="kuika-config-field__error">{error}</div>}
      </div>
      {(onRestore !== undefined || tooltipInMeta) && (
        <span className="kuika-config-field__meta">
          {onRestore && (
            <KuikaTooltip content="Restore" position="right">
              <button
                type="button"
                className="kuika-config-field__icon-button"
                aria-label="Restore the inherited value"
                onClick={onRestore}
              >
                <KuikaSvg name="restore" width={16} height={16} />
              </button>
            </KuikaTooltip>
          )}
          {tooltipInMeta && tooltipContentKey && <ConfigTooltip contentKey={tooltipContentKey} />}
        </span>
      )}
    </div>
  );
};
